import fileSize from 'filesize';
import { merge } from 'lodash';
import React, {
  DependencyList,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GUI } from 'three/examples/jsm/libs/lil-gui.module.min.js';
import { z } from 'zod';
import { LoadResult, getLoader, onLoad } from './loaders';
import { makeScene } from './makeScene';
import { lightDefaults } from './lightDefaults';
import { useLocalStorageBackedState } from './useLocalStoreBackedState';
import { downloadModel } from './modelDownloader';
import { resolveTextureUrl } from './textureResolvers';
import { autoLoadTextures } from './autoTextureLoader';

function useStableCallback<Params extends any[], Return>(
  callback: (...args: Params) => Return,
  deps: DependencyList,
): (...args: Params) => Return {
  const originalCallback = useCallback(callback, [...deps, callback]);

  const originalCallbackRef = useRef(originalCallback);
  // update ref when callback updates
  useEffect(() => {
    originalCallbackRef.current = originalCallback;
  }, [originalCallback]);

  // new callback that never changes
  const stableCallback = useCallback((...args: Params): Return => {
    return originalCallbackRef.current(...args);
  }, []);

  return stableCallback;
}

function useSettings() {
  type RecursivePartial<T> = {
    [K in keyof T]?: T[K] extends object ? RecursivePartial<T[K]> : T[K];
  };

  const schema = z.object({
    render: z.object({
      fov: z.number().min(1).max(90),
      grid: z.boolean(),
      transparent: z.boolean(),
      backgroundIntensity: z.number().min(0).max(1),
    }),
    lights: z.object({
      directional: z.boolean(),
      directionalIntensity: z.number().min(0).max(10),
      directionalPosition: z.tuple([z.number(), z.number(), z.number()]),
      hemi: z.boolean(),
      hemiIntensity: z.number().min(0).max(10),
      hemiSkyColor: z.tuple([
        z.number().min(0).max(1),
        z.number().min(0).max(1),
        z.number().min(0).max(1),
      ]),
      hemiGroundColor: z.tuple([
        z.number().min(0).max(1),
        z.number().min(0).max(1),
        z.number().min(0).max(1),
      ]),
      ambient: z.boolean(),
      ambientIntensity: z.number().min(0).max(10),
      ambientColor: z.tuple([
        z.number().min(0).max(1),
        z.number().min(0).max(1),
        z.number().min(0).max(1),
      ]),
    }),
    // default нужен, чтобы уже сохранённые в localStorage настройки без этой секции не сбрасывались
    material: z
      .object({
        unlit: z.boolean(),
      })
      .default({ unlit: false }),
  });
  type Settings = z.infer<typeof schema>;
  const defaultValue: Settings = {
    render: { fov: 5, grid: false, transparent: false, backgroundIntensity: 0.05 },
    lights: {
      ambient: true,
      directional: true,
      directionalIntensity: lightDefaults.directional.intensity,
      directionalPosition: lightDefaults.directional.position,
      hemi: true,
      hemiIntensity: lightDefaults.hemisphere.intensity,
      hemiSkyColor: lightDefaults.hemisphere.skyColor,
      hemiGroundColor: lightDefaults.hemisphere.groundColor,
      ambientIntensity: lightDefaults.ambient.intensity,
      ambientColor: lightDefaults.ambient.color,
    },
    material: { unlit: false },
  };
  const localStorageKey = 'creative hub 3d viewer';

  const [state, setState] = useLocalStorageBackedState<Settings>(
    defaultValue,
    schema,
    localStorageKey,
  );
  const setPartialState = useStableCallback(
    (value: RecursivePartial<Settings>) => {
      setState(merge({}, state, value));
    },
    [setState, state],
  );

  return [state, setPartialState] as const;
}

function getPreciseBoundingBox(object: THREE.Object3D): THREE.Box3 {
  const box = new THREE.Box3();
  let hasSkinnedMesh = false;

  object.traverse((child) => {
    // Skip invisible objects
    if (!child.visible) {
      return;
    }

    // Handle SkinnedMesh: use bone positions instead of geometry
    if (child instanceof THREE.SkinnedMesh && child.skeleton) {
      hasSkinnedMesh = true;
      const bones = child.skeleton.bones;
      for (let i = 0; i < bones.length; i++) {
        const bone = bones[i];
        const boneWorldPos = new THREE.Vector3();
        bone.getWorldPosition(boneWorldPos);
        box.expandByPoint(boneWorldPos);
      }
      return;
    }

    // Handle regular Mesh: sample geometry vertices
    if (child instanceof THREE.Mesh && child.geometry) {
      const positionAttribute = child.geometry.getAttribute('position');
      if (positionAttribute) {
        // Sample every 3rd vertex for performance (each vertex is 3 floats: x, y, z)
        const vertexCount = positionAttribute.count;
        const tempVec = new THREE.Vector3();

        for (let i = 0; i < vertexCount; i += 3) {
          tempVec.fromBufferAttribute(positionAttribute, i);
          // Transform vertex to world space
          tempVec.applyMatrix4(child.matrixWorld);
          box.expandByPoint(tempVec);
        }
      }
    }
  });

  // Mark the box if it came from skinned meshes for later expansion
  (box as any).__fromSkinnedMesh = hasSkinnedMesh;

  return box;
}

function fitCameraToSelection(
  camera: THREE.Camera,
  controls: OrbitControls | null,
  selection: THREE.Object3D,
  fitOffset: number = 0.9,
) {
  const box = getPreciseBoundingBox(selection);

  if (box.isEmpty()) {
    console.warn('fitCameraToSelection: bounding box is empty');
    return;
  }

  const fromSkinnedMesh = (box as any).__fromSkinnedMesh;

  // Expand box by ~15-20% if it came from skinned mesh to account for skin thickness
  if (fromSkinnedMesh) {
    const center = new THREE.Vector3();
    box.getCenter(center);
    box.expandByScalar(box.getSize(new THREE.Vector3()).length() * 0.1);
  }

  // Получаем полный центр объекта
  const boxCenter = new THREE.Vector3();
  box.getCenter(boxCenter);

  // 1. Определяем Pivot (Точка, куда смотрит камера)
  // Берем X и Z от мира (0), а Y от центра объекта.
  const pivot = new THREE.Vector3(boxCenter.x, boxCenter.y, boxCenter.z);

  // Угол обзора (изометрия/3 четверти)
  const fixedDirection = new THREE.Vector3(1, 0.5, 1).normalize();

  // 2. Вычисляем радиус для зума
  const objectSphere = new THREE.Sphere();
  box.getBoundingSphere(objectSphere);

  // Это позволит камере приблизиться вплотную к объекту, а не охватывать всю сцену.
  const effectiveRadius = objectSphere.radius;

  if (camera instanceof THREE.PerspectiveCamera) {
    if (camera.aspect <= 0) {
      console.warn('fitCameraToSelection: camera aspect ratio is invalid');
      return;
    }

    // 3. Считаем дистанцию через синус угла обзора (FOV)
    const fovRad = (camera.fov * Math.PI) / 180;
    const halfFov = fovRad / 2;

    const aspectFactor = camera.aspect < 1 ? camera.aspect : 1;

    // Формула для вписывания сферы в пирамиду видимости
    const distance = (effectiveRadius * fitOffset) / Math.sin(halfFov * aspectFactor);

    // 4. Позиционируем камеру
    // Камера встает на вектор fixedDirection относительно НОВОГО pivot
    camera.position.copy(fixedDirection).multiplyScalar(distance).add(pivot);
  }

  // 5. Смотрим на новый Pivot (по высоте объекта, но в центре мира по X/Z)
  camera.lookAt(pivot);

  // 6. Обновляем цель контролов
  if (controls) {
    controls.target.copy(pivot);
    controls.update();
  }
}

// Axis rotation types for the flip buttons
export type UpAxis = 'Y' | 'Z' | '-Y' | '-Z';

// Animation selection: null means show static mesh, string is animation name
export type AnimationSelection = string | null;

export function use3dHook(
  // canvasRef: React.RefObject<HTMLCanvasElement>,
  hostRef: React.RefObject<HTMLDivElement>,
  extension: string,
  src: string,
  filename: string,
  noGUI: boolean,
) {
  const [settings, setSettings] = useSettings();
  const [upAxis, setUpAxis] = useState<UpAxis>('Y');
  const [selectedAnimation, setSelectedAnimation] = useState<AnimationSelection>(null);
  const [cameraType] = useState<'perspective' | 'ortho'>('perspective');
  const dimensions = useDimensions(hostRef.current);
  const aspect =
    dimensions.w === null || dimensions.h === null || dimensions.w === 0 || dimensions.h === 0
      ? 1
      : dimensions.w / dimensions.h;

  // refs
  const controlsRef = useRef<OrbitControls | null>(null);
  //memos
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const scene = useMemo(
    () =>
      makeScene({
        type: cameraType === 'perspective' ? 'perspective' : 'orthographic',
        fov: settings.render.fov,
      }),
    [cameraType, settings.render.fov],
  );
  scene.groups.gridGroup.visible = settings.render.grid;
  scene.lights.AmbientLight.visible = settings.lights.ambient;
  scene.lights.AmbientLight.intensity = settings.lights.ambientIntensity;
  scene.lights.AmbientLight.color = new THREE.Color(...settings.lights.ambientColor);
  scene.lights.DirectionalLight.visible = settings.lights.directional;
  scene.lights.DirectionalLight.intensity = settings.lights.directionalIntensity;
  scene.lights.DirectionalLight.position.set(...settings.lights.directionalPosition);
  scene.lights.HemisphereLight.visible = settings.lights.hemi;
  scene.lights.HemisphereLight.intensity = settings.lights.hemiIntensity;
  scene.lights.HemisphereLight.color = new THREE.Color(...settings.lights.hemiSkyColor);
  scene.lights.HemisphereLight.groundColor = new THREE.Color(...settings.lights.hemiGroundColor);
  scene.scene.background = settings.render.transparent
    ? null
    : new THREE.Color(
        settings.render.backgroundIntensity,
        settings.render.backgroundIntensity,
        settings.render.backgroundIntensity,
      );
  const renderer = useMemo<THREE.WebGLRenderer | null>(() => {
    if (canvas === null) return null;
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      logarithmicDepthBuffer: true,
      preserveDrawingBuffer: true,
      canvas,
    });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Мягкие края теней

    // Без этого свет с интенсивностью 2.5 просто выжжет белый цвет в "плоское пятно"
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;

    return renderer;
  }, [canvas]);
  // states

  const loader = useLoader(src, extension, filename);
  const textureUrlsList = useMemo(() => {
    if (!loader.loadResult?.textureUrls) return [] as string[];
    return Array.from(loader.loadResult.textureUrls.values());
  }, [loader.loadResult?.textureUrls]);
  const loadedTexturesList = useMemo(() => {
    return loader.loadResult?.loadedTextures ?? [];
  }, [loader.loadResult?.loadedTextures]);
  const failedTexturesList = useMemo(() => {
    return loader.loadResult?.failedTextures ?? [];
  }, [loader.loadResult?.failedTextures]);
  const object = loader.loadResult?.object;
  const animations = loader.loadResult?.animations ?? [];
  const camera = scene.camera;

  // Update camera aspect ratio when dimensions change
  useEffect(() => {
    if (camera instanceof THREE.PerspectiveCamera && camera.aspect !== aspect) {
      camera.aspect = aspect;
      camera.updateProjectionMatrix();
    }
  }, [aspect, camera]);

  // Reset animation selection and vertical axis when loading a new model
  useEffect(() => {
    setUpAxis('Y');
    // If model has skinned meshes and animations, set first animation as default
    if (loader.loadResult?.hasSkinnedMesh && loader.loadResult?.animations.length > 0) {
      setSelectedAnimation(loader.loadResult.animations[0].name);
    } else {
      setSelectedAnimation(null);
    }
  }, [src, loader.loadResult?.hasSkinnedMesh, loader.loadResult?.animations]);

  useLayoutEffect(() => {
    if (loader.loadResult === null) {
      console.info('fx: scene clear: clear');
      scene.groups.hostGroup.clear();
    } else {
      console.info(
        'fx: scene update: showing',
        selectedAnimation !== null ? 'animated' : 'static',
        'mesh',
      );
      scene.groups.hostGroup.clear();
      const objToShow =
        selectedAnimation !== null ? loader.loadResult.object : loader.loadResult.staticObject;
      scene.groups.hostGroup.add(objToShow);

      // Position camera synchronously immediately after adding object to scene
      if (camera && controlsRef.current) {
        fitCameraToSelection(camera, controlsRef.current, objToShow);
      }

      // Ensure matrix world is up to date for the first render
      scene.scene.updateMatrixWorld(true);
    }
  }, [src, scene, loader.loadResult, selectedAnimation, camera]);

  useEffect(() => {
    console.info('fx: creating canvas element');
    if (hostRef.current) {
      const cur = hostRef.current;
      while (hostRef.current.firstChild) {
        hostRef.current.removeChild(hostRef.current.firstChild);
      }
      const newCanvas = document.createElement('canvas');
      cur.appendChild(newCanvas);
      setCanvas(newCanvas);
      return () => {
        cur.removeChild(newCanvas);
        setCanvas(null);
      };
    }
  }, [hostRef]);
  useEffect(() => {
    console.info('fx: set up to dispose renderer later...');
    return () => {
      console.info('renderer disposed.');
      renderer?.dispose();
    };
  }, [renderer]);
  useEffect(() => {
    console.info('fx: updating renderer size');
    if (renderer && dimensions.w !== null && dimensions.h !== null) {
      renderer.setSize(dimensions.w, dimensions.h);
    }
  }, [dimensions, renderer]);

  useEffect(() => {
    console.info('fx: updating controlsRef');
    if (renderer === null || camera === null) return;
    controlsRef.current = new OrbitControls(camera, renderer.domElement);
    controlsRef.current.target.set(0, 0, 0);
    controlsRef.current.update();
    return () => {
      if (controlsRef.current) {
        controlsRef.current.dispose();
      }
    };
  }, [renderer, camera]);
  useAnimation(loader.loadResult, renderer, camera, scene.scene, selectedAnimation);

  useEffect(() => {
    console.info('fx: creating gui');
    if (noGUI) {
      return;
    }
    const options = {
      wf: false,
      flat: false,
      unlit: settings.material.unlit,
      grid: settings.render.grid,
      transparent: settings.render.transparent,
      brightness: settings.render.backgroundIntensity,
      fov: camera instanceof THREE.PerspectiveCamera ? camera.fov : 0,
    };
    const gui = new GUI({ title: 'Control panel' });

    const render = gui.addFolder('Render');
    render.open(false);
    render
      .add(options, 'grid')
      .name('Grid')
      .onChange((newGridVisible) => {
        scene.groups.gridGroup.visible = newGridVisible;
        setSettings({ render: { grid: newGridVisible } });
      });
    render
      .add(options, 'transparent')

      .name('Transparent')
      .onChange((newTransparent) => {
        setSettings({ render: { transparent: newTransparent } });
        scene.scene.background = newTransparent
          ? null
          : new THREE.Color(options.brightness, options.brightness, options.brightness);
      });
    render
      .add(options, 'brightness', 0.03, 0.55, 0.01)
      .onChange((newBrightness) => {
        if (!options.transparent) {
          scene.scene.background = new THREE.Color(newBrightness, newBrightness, newBrightness);
        }
        setSettings({ render: { backgroundIntensity: newBrightness } });
      })
      .name('Background Intensity');
    render
      .add(
        {
          reset: () => {
            options.grid = false;
            options.transparent = false;
            options.brightness = 0.03;
            scene.groups.gridGroup.visible = false;
            scene.scene.background = new THREE.Color(0.03, 0.03, 0.03);
            setSettings({
              render: {
                grid: false,
                transparent: false,
                backgroundIntensity: 0.03,
              },
            });
            render.controllers.forEach((controller) => controller.updateDisplay());
          },
        },
        'reset',
      )
      .name('Reset Render Settings');
    render
      .add(options, 'fov', 1, 60, 1)
      .onChange((newFov) => {
        if (camera instanceof THREE.PerspectiveCamera) {
          camera.fov = newFov; // Set the new FOV value (in degrees)
          camera.updateProjectionMatrix(); // Update the camera's projection matrix after changing FOV
          setSettings({ render: { fov: newFov } });
        }
      })
      .name('FOV');
    //lights
    const lights = gui.addFolder('Scene lights');
    lights.open(false);
    lights
      .add(scene.lights.DirectionalLight, 'visible')
      .name('Directional')
      .onChange((newVisible) => {
        setSettings({ lights: { directional: newVisible } });
      });
    lights
      .add(scene.lights.DirectionalLight, 'intensity', 0, 5, 0.1)
      .name('Dir. light intensity')
      .onChange((newIntensity) => {
        setSettings({ lights: { directionalIntensity: newIntensity } });
      });

    const dirPosFolder = lights.addFolder('Dir. light position');
    dirPosFolder
      .add(
        {
          x: settings.lights.directionalPosition[0],
        },
        'x',
        -15,
        15,
        0.1,
      )
      .onChange((value) => {
        setSettings({
          lights: {
            directionalPosition: [
              value,
              settings.lights.directionalPosition[1],
              settings.lights.directionalPosition[2],
            ],
          },
        });
      });
    dirPosFolder
      .add(
        {
          y: settings.lights.directionalPosition[1],
        },
        'y',
        -20,
        20,
        0.1,
      )
      .onChange((value) => {
        setSettings({
          lights: {
            directionalPosition: [
              settings.lights.directionalPosition[0],
              value,
              settings.lights.directionalPosition[2],
            ],
          },
        });
      });
    dirPosFolder
      .add(
        {
          z: settings.lights.directionalPosition[2],
        },
        'z',
        -25,
        25,
        0.1,
      )
      .onChange((value) => {
        setSettings({
          lights: {
            directionalPosition: [
              settings.lights.directionalPosition[0],
              settings.lights.directionalPosition[1],
              value,
            ],
          },
        });
      });

    lights
      .add(scene.lights.HemisphereLight, 'visible')
      .name('Hemisphere')
      .onChange((newVisible) => {
        setSettings({ lights: { hemi: newVisible } });
      });
    lights
      .add(scene.lights.HemisphereLight, 'intensity', 0, 3, 0.01)
      .name('Hemi. light intensity')
      .onChange((newIntensity) => {
        setSettings({ lights: { hemiIntensity: newIntensity } });
      });
    lights
      .addColor(
        {
          color: new THREE.Color(...settings.lights.hemiSkyColor).getHex(),
        },
        'color',
      )
      .name('Hemi. sky color')
      .onChange((color) => {
        const c = new THREE.Color(color);
        setSettings({ lights: { hemiSkyColor: [c.r, c.g, c.b] } });
      });
    lights
      .addColor(
        {
          color: new THREE.Color(...settings.lights.hemiGroundColor).getHex(),
        },
        'color',
      )
      .name('Hemi. ground color')
      .onChange((color) => {
        const c = new THREE.Color(color);
        setSettings({ lights: { hemiGroundColor: [c.r, c.g, c.b] } });
      });

    lights
      .add(scene.lights.AmbientLight, 'visible')
      .name('Ambient')
      .onChange((newVisible) => {
        setSettings({ lights: { ambient: newVisible } });
      });
    lights
      .add(scene.lights.AmbientLight, 'intensity', 0, 3, 0.01)
      .name('Amb. light intensity')
      .onChange((newIntensity) => {
        setSettings({ lights: { ambientIntensity: newIntensity } });
      });
    lights
      .addColor(scene.lights.AmbientLight, 'color')
      .name('Amb. light color')
      .onChange((color) => {
        setSettings({ lights: { ambientColor: [color.r, color.g, color.b] } });
      });

    lights
      .add(
        {
          reset: () => {
            setSettings({
              lights: {
                directional: true,
                directionalIntensity: lightDefaults.directional.intensity,
                directionalPosition: lightDefaults.directional.position,
                hemi: true,
                hemiIntensity: lightDefaults.hemisphere.intensity,
                hemiSkyColor: lightDefaults.hemisphere.skyColor,
                hemiGroundColor: lightDefaults.hemisphere.groundColor,
                ambient: true,
                ambientIntensity: lightDefaults.ambient.intensity,
                ambientColor: lightDefaults.ambient.color,
              },
            });
            lights.controllers.forEach((controller) => controller.updateDisplay());
          },
        },
        'reset',
      )
      .name('Reset Light Settings');

    // Material
    const materialGUI = gui.addFolder('Material');
    materialGUI
      .add(options, 'unlit')
      .name('Ignore lights')
      .onChange((value: boolean) => {
        setSettings({ material: { unlit: value } });
      });
    materialGUI
      .add(options, 'flat')
      .name('Flat shading')
      .onChange((value) => {
        object?.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            if (materialHasFlatMap(child.material)) {
              if (child.material.flatShading !== value) {
                child.material.flatShading = value;
                child.material.needsUpdate = true;
              }
            }
          }
        });
      });
    materialGUI
      .add(options, 'wf')
      .name('Wireframe')
      .onChange((value) => {
        object?.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            if (materialHasWireframe(child.material)) {
              if (child.material.wireframe !== value) {
                child.material.wireframe = value;
                child.material.needsUpdate = true;
              }
            }
          }
        });
      });

    // Textures - show loaded and failed textures from loader
    const totalTextures = loadedTexturesList.length + failedTexturesList.length;
    if (totalTextures > 0 || textureUrlsList.length > 0) {
      // Build custom HTML for all textures in one block
      let texturesHtml = `<div style="font-size: 0.9em; font-weight: bold; color: #fff; margin-bottom: 12px;">Textures (${totalTextures})</div>`;

      if (loadedTexturesList.length > 0) {
        texturesHtml += `<div style="margin-bottom: 12px;">
          <div style="font-size: 0.85em; font-weight: bold; color: #90ee90; margin-bottom: 6px;">✓ Loaded (${loadedTexturesList.length})</div>`;
        loadedTexturesList.forEach((filename) => {
          texturesHtml += `<div style="margin-bottom: 4px;"><input type="text" value="${filename.replace(/"/g, '&quot;')}" readonly style="width: 100%; padding: 4px; font-size: 0.75em; background: #333; color: #ccc; border: 1px solid #555; border-radius: 2px; box-sizing: border-box; font-family: monospace; cursor: text;" /></div>`;
        });
        texturesHtml += '</div>';
      }

      if (failedTexturesList.length > 0) {
        texturesHtml += `<div>
          <div style="font-size: 0.85em; font-weight: bold; color: #ffaa55; margin-bottom: 6px;">✗ Not loaded (${failedTexturesList.length})</div>`;
        failedTexturesList.forEach((filename) => {
          texturesHtml += `<div style="margin-bottom: 4px;"><input type="text" value="${filename.replace(/"/g, '&quot;')}" readonly style="width: 100%; padding: 4px; font-size: 0.75em; background: #333; color: #ccc; border: 1px solid #555; border-radius: 2px; box-sizing: border-box; font-family: monospace; cursor: text;" /></div>`;
        });
        texturesHtml += '</div>';
      }

      const texturesElement = document.createElement('div');
      texturesElement.style.padding = '8px';
      texturesElement.style.marginTop = '12px';
      texturesElement.style.borderTop = '1px solid #555';
      texturesElement.innerHTML = texturesHtml;
      materialGUI.domElement.appendChild(texturesElement);
    }

    const materialRoots: THREE.Object3D[] = [object, loader.loadResult?.staticObject].filter(
      (value): value is THREE.Group => value !== undefined,
    );

    if (materialRoots.length > 0) {
      const materialEntries = getMaterialsList(materialRoots);
      if (materialEntries.length > 0) {
        const materialsElement = document.createElement('div');
        materialsElement.style.padding = '8px';
        materialsElement.style.marginTop = '12px';
        materialsElement.style.borderTop = '1px solid #555';

        const materialsTitle = document.createElement('div');
        materialsTitle.style.fontSize = '0.9em';
        materialsTitle.style.fontWeight = 'bold';
        materialsTitle.style.color = '#fff';
        materialsTitle.style.marginBottom = '12px';
        materialsTitle.innerText = `Materials (${materialEntries.length})`;
        materialsElement.appendChild(materialsTitle);

        materialEntries.forEach(({ name, materials }) => {
          const row = document.createElement('div');
          row.style.display = 'flex';
          row.style.flexDirection = 'column';
          row.style.gap = '6px';
          row.style.marginBottom = '6px';

          const nameLabel = document.createElement('div');
          nameLabel.innerText = name;
          nameLabel.title = name;
          nameLabel.style.padding = '4px 6px';
          nameLabel.style.fontSize = '0.75em';
          nameLabel.style.background = '#333';
          nameLabel.style.color = '#ccc';
          nameLabel.style.border = '1px solid #555';
          nameLabel.style.borderRadius = '2px';
          nameLabel.style.boxSizing = 'border-box';
          nameLabel.style.fontFamily = 'monospace';
          nameLabel.style.whiteSpace = 'nowrap';
          nameLabel.style.overflow = 'hidden';
          nameLabel.style.textOverflow = 'ellipsis';
          nameLabel.style.width = '100%';

          const loadButton = document.createElement('button');
          loadButton.type = 'button';
          loadButton.innerText = 'Override texture';
          loadButton.style.width = '100%';
          loadButton.style.padding = '4px 8px';
          loadButton.style.fontSize = '0.75em';
          loadButton.style.background = '#444';
          loadButton.style.color = '#eee';
          loadButton.style.border = '1px solid #666';
          loadButton.style.borderRadius = '2px';
          loadButton.style.cursor = 'pointer';

          loadButton.onclick = async () => {
            const inputName = window.prompt('Texture filename', `${name}.png`);
            if (inputName === null) {
              return;
            }

            const textureFilename = inputName.trim();
            if (textureFilename.length === 0) {
              return;
            }

            const originalTitle = loadButton.innerText;
            loadButton.disabled = true;
            loadButton.innerText = 'Loading...';

            try {
              const textureUrl = resolveTextureUrl(textureFilename, src);
              const texture = await new THREE.TextureLoader().loadAsync(textureUrl);
              texture.colorSpace = THREE.SRGBColorSpace;

              materials.forEach((material) => {
                if (materialHasMap(material)) {
                  material.map = texture;
                  material.needsUpdate = true;
                }
              });

              console.info(
                `[3d Viewer] Material "${name}" updated with texture: ${textureFilename} -> ${textureUrl}`,
              );
            } catch (error) {
              console.error(`[3d Viewer] Failed to update texture for material "${name}"`, error);
              window.alert(`Failed to load texture for material "${name}"`);
            } finally {
              loadButton.disabled = false;
              loadButton.innerText = originalTitle;
            }
          };

          row.appendChild(nameLabel);
          row.appendChild(loadButton);
          materialsElement.appendChild(row);
        });

        materialGUI.domElement.appendChild(materialsElement);
      }
    }

    return () => {
      console.info('disposing controls');
      gui.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    object,
    loader.loadResult?.staticObject,
    noGUI,
    camera,
    scene,
    setSettings,
    src,
    textureUrlsList,
    loadedTexturesList,
    failedTexturesList,
  ]);

  // Режим "как есть": игнорируем освещение — подменяем материалы на unlit (MeshBasicMaterial),
  // чтобы текстура попадала на экран без света, теней и тонмаппинга
  useEffect(() => {
    const roots = [loader.loadResult?.object, loader.loadResult?.staticObject].filter(
      (value): value is THREE.Group => value !== undefined && value !== null,
    );
    if (roots.length === 0) {
      return;
    }
    if (settings.material.unlit) {
      roots.forEach(applyUnlitMaterials);
    } else {
      roots.forEach(restoreLitMaterials);
    }
    // при смене модели/размонтировании возвращаем исходные материалы
    return () => {
      roots.forEach(restoreLitMaterials);
    };
  }, [loader.loadResult, settings.material.unlit]);

  // Apply axis rotation to the model
  useEffect(() => {
    if (loader.loadResult === null) return;
    const obj = loader.loadResult.object;

    // Reset rotation first
    obj.rotation.set(0, 0, 0);

    // Apply rotation based on selected up axis
    switch (upAxis) {
      case 'Y':
        // Default Y-up, no rotation needed
        break;
      case 'Z':
        // Z-up to Y-up: rotate -90 degrees around X
        obj.rotation.x = -Math.PI / 2;
        break;
      case '-Y':
        // Flip upside down: rotate 180 degrees around X
        obj.rotation.x = Math.PI;
        break;
      case '-Z':
        // -Z-up: rotate 90 degrees around X
        obj.rotation.x = Math.PI / 2;
        break;
    }

    obj.updateMatrixWorld(true);

    // Also apply same rotation to static object
    if (loader.loadResult.staticObject) {
      loader.loadResult.staticObject.rotation.copy(obj.rotation);
      loader.loadResult.staticObject.updateMatrixWorld(true);
    }

    // Refit camera after rotation
    if (camera && controlsRef.current) {
      requestAnimationFrame(() => {
        const objToFit = selectedAnimation !== null ? obj : loader.loadResult!.staticObject;
        fitCameraToSelection(camera, controlsRef.current, objToFit);
      });
    }
  }, [
    upAxis,
    loader.loadResult,
    camera,
    selectedAnimation,
    settings.render.backgroundIntensity,
    settings.render.grid,
    settings.render.transparent,
  ]);

  // Control functions to expose
  const setUpAxisTo = useCallback((axis: UpAxis) => {
    setUpAxis(axis);
  }, []);

  const zoomToFit = useCallback(() => {
    if (loader.loadResult && camera && controlsRef.current) {
      const objToFit =
        selectedAnimation !== null ? loader.loadResult.object : loader.loadResult.staticObject;
      fitCameraToSelection(camera, controlsRef.current, objToFit);
    }
  }, [loader.loadResult, camera, selectedAnimation]);

  const selectAnimation = useCallback((animationName: AnimationSelection) => {
    setSelectedAnimation(animationName);
  }, []);

  const toggleGrid = useCallback(() => {
    setSettings({ render: { grid: !settings.render.grid } });
  }, [settings.render.grid, setSettings]);

  const toggleTransparent = useCallback(() => {
    setSettings({ render: { transparent: !settings.render.transparent } });
  }, [settings.render.transparent, setSettings]);

  const resetRenderSettings = useCallback(() => {
    setSettings({
      render: {
        grid: false,
        transparent: false,
        backgroundIntensity: 0.03,
      },
    });
  }, [setSettings]);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!loader.loadResult?.object) {
      console.warn('No model loaded to download');
      return;
    }

    setIsDownloading(true);
    try {
      const objToExport =
        selectedAnimation !== null ? loader.loadResult.object : loader.loadResult.staticObject;
      await downloadModel(objToExport, filename, renderer!, camera);
    } catch (error) {
      console.error('Failed to download model:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [loader.loadResult, selectedAnimation, filename, camera, renderer]);

  return {
    ...loader,
    animations,
    selectedAnimation,
    controls: {
      upAxis,
      setUpAxisTo,
      zoomToFit,
      selectAnimation,
      toggleGrid,
      toggleTransparent,
      resetRenderSettings,
      gridVisible: settings.render.grid,
      transparentBackground: settings.render.transparent,
      backgroundIntensity: settings.render.backgroundIntensity,
      handleDownload,
      isDownloading,
    },
  };
}

function useAnimation(
  loadResult: LoadResult | null,
  renderer: THREE.WebGLRenderer | null,
  camera: THREE.Camera | null,
  scene: THREE.Scene,
  selectedAnimation: AnimationSelection,
) {
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);

  useEffect(() => {
    console.info('fx: [useAnimation] updating mixerRef, selectedAnimation:', selectedAnimation);

    // If no animation is selected, don't set up the mixer
    // The static mesh will be shown instead
    if (selectedAnimation === null || loadResult === null || loadResult.object === null) {
      mixerRef.current?.stopAllAction();
      mixerRef.current = null;
      return;
    }

    // Fix for FBX models: update world matrices and skeleton bind matrices
    // This ensures proper bone positioning after loading
    loadResult.object.updateMatrixWorld(true);

    loadResult.object.traverse(function (child) {
      if (child instanceof THREE.SkinnedMesh && child.skeleton) {
        // Ensure the skeleton's bone matrices are properly calculated
        // by updating from the current bone world matrices
        //child.skeleton.calculateInverses();
        child.skeleton.update();
        console.log(
          `Updated skeleton for mesh "${child.name}" with ${child.skeleton.bones.length} bones`,
        );
      }
    });

    mixerRef.current = new THREE.AnimationMixer(loadResult.object);

    // Find and play the selected animation
    const animations = loadResult.animations;
    const animToPlay = animations.find((a) => a.name === selectedAnimation);

    if (animToPlay) {
      console.info(`Playing animation: ${animToPlay.name}`, animToPlay);
      const action = mixerRef.current.clipAction(animToPlay);
      if (action) {
        action.play();
        // Force update to frame 0 to ensure correct initial pose
        action.time = 0;
      }

      // Force an initial update of the mixer to apply the first frame
      mixerRef.current.update(0);
    } else {
      console.warn(`Animation "${selectedAnimation}" not found`);
    }

    return () => {
      mixerRef.current?.stopAllAction();
      mixerRef.current = null;
    };
  }, [loadResult, selectedAnimation]);

  useEffect(() => {
    console.info('fx: [useAnimation] animating');
    const clock = new THREE.Clock();
    let req: number;

    function animateLoop() {
      mixerRef.current?.update(clock.getDelta());
      if (renderer && camera) {
        renderer.render(scene, camera);
      }

      req = requestAnimationFrame(animateLoop);
    }

    req = requestAnimationFrame(animateLoop);

    return () => {
      cancelAnimationFrame(req);
    };
  }, [renderer, scene, camera]);
}

function materialHasFlatMap(m: THREE.Material) {
  return (
    m instanceof THREE.MeshLambertMaterial ||
    m instanceof THREE.MeshMatcapMaterial ||
    m instanceof THREE.MeshNormalMaterial ||
    m instanceof THREE.MeshPhongMaterial ||
    m instanceof THREE.MeshStandardMaterial
  );
}

function materialHasMap(m: THREE.Material): m is THREE.Material & { map: THREE.Texture | null } {
  return 'map' in m;
}

function materialHasWireframe(m: THREE.Material): m is THREE.Material & { wireframe: boolean } {
  return 'wireframe' in m;
}

function toMaterialArray(material: THREE.Material | THREE.Material[]): THREE.Material[] {
  return Array.isArray(material) ? material : [material];
}

/** Исходные (освещаемые) материалы мешей, подменённых на unlit-версии */
const litMaterialsBackup = new WeakMap<THREE.Mesh, THREE.Material | THREE.Material[]>();

/**
 * Unlit-копия материала: тот же diffuse-map, но без расчёта освещения и без
 * тонмаппинга — пиксели текстуры выводятся так, как лежат в файле.
 */
function createUnlitMaterial(source: THREE.Material): THREE.MeshBasicMaterial {
  const unlit = new THREE.MeshBasicMaterial();
  unlit.name = source.name;
  unlit.transparent = source.transparent;
  unlit.opacity = source.opacity;
  unlit.alphaTest = source.alphaTest;
  unlit.alphaHash = source.alphaHash;
  unlit.blending = source.blending;
  unlit.premultipliedAlpha = source.premultipliedAlpha;
  unlit.depthTest = source.depthTest;
  unlit.depthWrite = source.depthWrite;
  unlit.side = source.side;
  unlit.visible = source.visible;
  unlit.vertexColors = source.vertexColors;
  // рендерер работает с ACESFilmicToneMapping, для режима "как есть" его нужно отключить
  unlit.toneMapped = false;
  unlit.fog = false;

  const map = materialHasMap(source) ? source.map : null;
  unlit.map = map;
  if ('alphaMap' in source) {
    unlit.alphaMap = (source as THREE.MeshStandardMaterial).alphaMap;
  }
  if (materialHasWireframe(source)) {
    unlit.wireframe = source.wireframe;
  }
  // с текстурой цвет оставляем белым, иначе тинт материала исказит её пиксели;
  // без текстуры цвет материала — единственный источник изображения, его сохраняем
  if (map === null && 'color' in source) {
    const sourceColor = (source as THREE.MeshStandardMaterial).color;
    if (sourceColor instanceof THREE.Color) {
      unlit.color.copy(sourceColor);
    }
  }

  return unlit;
}

function applyUnlitMaterials(root: THREE.Object3D): void {
  root.traverse((child) => {
    if (!(child instanceof THREE.Mesh) || litMaterialsBackup.has(child)) {
      return;
    }
    const original: THREE.Material | THREE.Material[] = child.material;
    litMaterialsBackup.set(child, original);
    child.material = Array.isArray(original)
      ? original.map(createUnlitMaterial)
      : createUnlitMaterial(original);
  });
}

function restoreLitMaterials(root: THREE.Object3D): void {
  root.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) {
      return;
    }
    const original = litMaterialsBackup.get(child);
    if (original === undefined) {
      return;
    }
    const unlitMaterials = toMaterialArray(child.material);
    // текстуру, подменённую через "Override texture" в unlit-режиме, переносим на исходный материал
    toMaterialArray(original).forEach((originalMaterial, index) => {
      const unlitMaterial = unlitMaterials[index];
      if (
        unlitMaterial !== undefined &&
        materialHasMap(originalMaterial) &&
        materialHasMap(unlitMaterial) &&
        originalMaterial.map !== unlitMaterial.map
      ) {
        originalMaterial.map = unlitMaterial.map;
        originalMaterial.needsUpdate = true;
      }
    });
    child.material = original;
    litMaterialsBackup.delete(child);
    // dispose освобождает только сами материалы, текстуры остаются жить в исходных
    unlitMaterials.forEach((material) => material.dispose());
  });
}

function getMaterialsList(
  objects: THREE.Object3D[],
): Array<{ name: string; materials: THREE.Material[] }> {
  const materialMap = new Map<string, THREE.Material[]>();

  objects.forEach((object) => {
    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const meshMaterials = Array.isArray(child.material) ? child.material : [child.material];
        meshMaterials.forEach((material) => {
          const materialName = material.name || 'Unnamed Material';
          const existing = materialMap.get(materialName);
          if (!existing) {
            materialMap.set(materialName, [material]);
            return;
          }
          if (!existing.includes(material)) {
            existing.push(material);
          }
        });
      }
    });
  });

  return Array.from(materialMap.entries())
    .map(([name, materials]) => ({ name, materials }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function useDimensions(host: HTMLElement | null) {
  const [w, setW] = useState<number | null>(null);
  const [h, setH] = useState<number | null>(null);
  const onResize = useCallback(() => {
    const hostW = host?.clientWidth ?? null;
    const hostH = host?.clientHeight ?? null;
    setW(hostW);
    setH(hostH);
  }, [host]);
  useEffect(() => {
    console.info('fx: [useDimensions] updating w,h');
    if (host === null) {
      setW(null);
      setH(null);
    } else {
      const observer = new ResizeObserver(onResize);
      observer.observe(host);
      onResize();
      return () => {
        observer.unobserve(host);
      };
    }
  }, [host, onResize]);
  const dimensions = React.useMemo(() => ({ w, h }), [w, h]);
  return dimensions;
}

function useLoader(src: string, extension: string, filename: string) {
  const [progress, setProgress] = useState<string | null>(null);
  const [loadResult, setLoadResult] = useState<LoadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  // reload models
  useEffect(() => {
    console.info('fx: [useLoader] main effect');
    const loader = getLoader(extension);
    if (loader === null) return;

    // Set the original filename for archive storage
    if ('setOriginalFilename' in loader && typeof loader.setOriginalFilename === 'function') {
      loader.setOriginalFilename(filename);
    }

    console.info('[3d Viewer] loading model:', src);

    const ac = new AbortController();
    let isMounted = true;
    setWarnings([]); // Clear warnings on new load

    const load = async () => {
      try {
        const object = await loader.loadAsync(src, (event) => {
          if (!isMounted) return;
          if (event.total === 0) {
            setProgress(`loaded ${fileSize(event.loaded)}`);
          } else {
            //keep one decimal place and always show zero (e.g. 12.0%)
            const percentage = (Math.round((event.loaded / event.total) * 1000) / 10).toFixed(1);
            setProgress(`loaded ${percentage}%`);
          }
        });
        if (!isMounted || ac.signal.aborted) return;

        const loadResult = onLoad(object, {
          textureCache: (object as any).__textureCache,
          loaderWarnings: (object as any).__loaderWarnings || [],
        });
        // Merge missing textures from logs with those detected during material check
        const missingFromLogs = (object as any).__missingTextures || [];
        loadResult.missingTextures = [
          ...new Set([...loadResult.missingTextures, ...missingFromLogs]),
        ];
        // Capture texture loading info from loader
        const textureInfo = (object as any).__textureInfo;
        if (textureInfo) {
          loadResult.requestedTextures = textureInfo.requestedTextures || [];
          loadResult.loadedTextures = textureInfo.loadedTextures || [];
          loadResult.failedTextures = textureInfo.failedTextures || [];
        }

        // Attempt to auto-load missing textures
        await autoLoadTextures(
          loadResult,
          src,
          (msg) => {
            if (isMounted) setProgress(msg);
          },
          (msg) => {
            if (isMounted) setWarnings((prev) => [...prev, msg]);
          },
        );

        if (!isMounted || ac.signal.aborted) return;

        setError(null);
        setProgress(null);
        setLoadResult(loadResult);
      } catch (reason) {
        if (!isMounted || ac.signal.aborted) return;

        let err = 'Failed to load the file';
        console.error('reason:');
        console.error(reason);
        try {
          err = String(((reason as ProgressEvent).target as XMLHttpRequest).statusText);
        } catch {
          //
        }
        setError(err);
        setProgress(null);
        setLoadResult(null);
      }
    };

    load();

    return () => {
      isMounted = false;
      ac.abort();
    };
  }, [src, extension, filename]);
  return { loadResult, progress, error, warnings };
}
